import React from "react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar"; // Import Navbar component
import swimmingImage from "../assets/images/swimming.avif";
import musicDramaImage from "../assets/images/music-drama.avif";
import scoutingImage from "../assets/images/scouting.jpg";
import sportsImage from "../assets/images/sports.jpg";
import academicImage from "../assets/images/academic.jpg";
import computerImage from "../assets/images/computer.jpg";
import mentoringImage from "../assets/images/mentoring.jpeg";
import graduationImage from "../assets/images/graduation.jpeg";
import awardsImage from "../assets/images/awards.jpeg";
import publicSpeakingImage from "../assets/images/public-speaking.jpeg";

const Admissions = () => {
  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="bg-gray-100 min-h-screen pt-28"
    >
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <motion.div 
        initial={{ y: -50, opacity: 0 }} 
        animate={{ y: 0, opacity: 1 }} 
        transition={{ duration: 1 }}
        className="relative bg-blue-100 py-16 px-6 text-center"
      >
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold text-blue-900 mb-6">Admissions</h1>
          <p className="text-lg text-gray-700 leading-relaxed">
            Discover the diverse academic and extracurricular opportunities at Joy Gardens School.
          </p>
        </div>
      </motion.div>

      {/* Academic Activities Section */}
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ duration: 1, delay: 0.5 }}
        className="container mx-auto px-6 md:px-16 py-12"
      >
        <h2 className="text-3xl font-bold text-blue-900 mb-8">Featured Academic Activities</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Academic Activity Cards */}
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <img src={academicImage} alt="Academic Program" className="w-full h-48 object-cover" />
            <div className="p-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">CBC Curriculum</h3>
              <p className="text-gray-700">
                Offering Day Care, PP1, PP2, Grade 1-6 under CBC, and Classes 7-8 under the 8-4-4 curriculum. Laying a strong foundation for future success.
              </p>
            </div>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <img src={computerImage} alt="Computer Classes" className="w-full h-48 object-cover" />
            <div className="p-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Computer Classes</h3>
              <p className="text-gray-700">
                Introduction to computer packages to equip learners with essential digital skills.
              </p>
            </div>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <img src={mentoringImage} alt="Mentorship Programs" className="w-full h-48 object-cover" />
            <div className="p-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Mentorship Programs</h3>
              <p className="text-gray-700">
                Partnering with scholars and icons to enhance learners' ambitions and perfect talents.
              </p>
            </div>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <img src={graduationImage} alt="Graduation Ceremony" className="w-full h-48 object-cover" />
            <div className="p-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Graduation Ceremony</h3>
              <p className="text-gray-700">
                Celebrating learners' achievements with pomp, color, and thanksgiving.
              </p>
            </div>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <img src={awardsImage} alt="Learners Awards" className="w-full h-48 object-cover" />
            <div className="p-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Learners Awards</h3>
              <p className="text-gray-700">
                Rewarding excellence and outstanding performance to encourage internal competition.
              </p>
            </div>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <img src={publicSpeakingImage} alt="Public Speaking" className="w-full h-48 object-cover" />
            <div className="p-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Public Speaking</h3>
              <p className="text-gray-700">
                Enhancing public speaking skills through creative pedagogy methods.
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Extracurricular Activities Section */}
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ duration: 1, delay: 1 }}
        className="bg-gray-200 py-12"
      >
        <div className="container mx-auto px-6 md:px-16">
          <h2 className="text-3xl font-bold text-blue-900 mb-8">Extracurricular Activities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <img src={swimmingImage} alt="Swimming" className="w-full h-48 object-cover" />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-blue-900 mb-4">Swimming</h3>
              </div>
            </motion.div>

            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <img src={musicDramaImage} alt="Music & Drama" className="w-full h-48 object-cover" />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-blue-900 mb-4">Music & Drama</h3>
              </div>
            </motion.div>

            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <img src={scoutingImage} alt="Scouting" className="w-full h-48 object-cover" />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-blue-900 mb-4">Scouting</h3>
              </div>
            </motion.div>

            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <img src={sportsImage} alt="Sports & Athletics" className="w-full h-48 object-cover" />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-blue-900 mb-4">Sports & Athletics</h3>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Downloads Section */}
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ duration: 1, delay: 1.5 }}
        className="bg-white py-12"
      >
        <div className="container mx-auto px-6 md:px-16">
          <h2 className="text-3xl font-bold text-blue-900 mb-8">Downloads</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="bg-gray-100 p-6 rounded-lg shadow-lg text-center"
            >
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Fee Structure</h3>
              <p className="text-gray-700 mb-4">Get the latest fee structure for the academic year.</p>
              <a
                href="/downloads/joy-fee-structure.pdf"
                className="bg-blue-600 text-white px-4 py-2 rounded-full font-medium shadow-md hover:bg-blue-700 transition"
                download
              >
                Download
              </a>
            </motion.div>

            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="bg-gray-100 p-6 rounded-lg shadow-lg text-center"
            >
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Brochure</h3>
              <p className="text-gray-700 mb-4">Learn more about our programs and facilities.</p>
              <a
                href="/downloads/brochure.pdf"
                className="bg-blue-600 text-white px-4 py-2 rounded-full font-medium shadow-md hover:bg-blue-700 transition"
                download
              >
                Download
              </a>
            </motion.div>

            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="bg-gray-100 p-6 rounded-lg shadow-lg text-center"
            >
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Application Form</h3>
              <p className="text-gray-700 mb-4">Fill out our application form to join us.</p>
              <a
                href="/downloads/application-form.pdf"
                className="bg-blue-600 text-white px-4 py-2 rounded-full font-medium shadow-md hover:bg-blue-700 transition"
                download
              >
                Download
              </a>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default Admissions;
